import React, { useCallback, useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { getSceneObjectDimensions } from "./sceneObjectSizes";

interface SceneObjectItemProps {
  id: string;
  objectId: string;
  name?: string;
  x: number;
  y: number;
  canControl: boolean;
  sceneSize: { width: number; height: number };
  onDrag: (id: string, x: number, y: number) => void;
  onDelete: (id: string) => void;
}

const SceneObjectItem: React.FC<SceneObjectItemProps> = ({
  id,
  objectId,
  name,
  x,
  y,
  canControl,
  sceneSize,
  onDrag,
  onDelete,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const itemRef = useRef<HTMLDivElement>(null);

  const isMobile = sceneSize.width < 768;
  const { width: objectWidth, height: objectHeight } = getSceneObjectDimensions(objectId, isMobile);

  const handleDelete = useCallback(() => {
    if (!canControl) return;
    Swal.fire({
      title: "Remover objeto?",
      text: name ? `"${name}" será removido do cenário.` : "O objeto será removido do cenário.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc2626",
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(id);
        setShowMenu(false);
      }
    });
  }, [canControl, id, name, onDelete]);

  // --- Mouse drag ---
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!canControl) return;
      if (showMenu) return;
      e.preventDefault();
      e.stopPropagation();
      const rect = itemRef.current?.getBoundingClientRect();
      if (!rect) return;
      dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
      setIsDragging(true);
      setShowMenu(false);
    },
    [canControl, showMenu]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const sceneEl = itemRef.current?.parentElement;
      if (!sceneEl) return;
      const rect = sceneEl.getBoundingClientRect();
      const newX = e.clientX - rect.left - dragOffsetRef.current.x;
      const newY = e.clientY - rect.top - dragOffsetRef.current.y;
      const clampedX = Math.max(0, Math.min(sceneSize.width - objectWidth, newX));
      const clampedY = Math.max(0, Math.min(sceneSize.height - objectHeight, newY));
      onDrag(id, clampedX, clampedY);
    };

    const handleMouseUp = () => setIsDragging(false);

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, id, onDrag, sceneSize, objectWidth, objectHeight]);

  // --- Touch drag ---
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (!canControl) return;
      if (showMenu) return;
      e.stopPropagation();
      const touch = e.touches[0];
      const rect = itemRef.current?.getBoundingClientRect();
      if (!rect) return;
      dragOffsetRef.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
      setIsDragging(true);
      setShowMenu(false);
    },
    [canControl, showMenu]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];
      const sceneEl = itemRef.current?.parentElement;
      if (!sceneEl) return;
      const rect = sceneEl.getBoundingClientRect();
      const newX = touch.clientX - rect.left - dragOffsetRef.current.x;
      const newY = touch.clientY - rect.top - dragOffsetRef.current.y;
      const clampedX = Math.max(0, Math.min(sceneSize.width - objectWidth, newX));
      const clampedY = Math.max(0, Math.min(sceneSize.height - objectHeight, newY));
      onDrag(id, clampedX, clampedY);
    };

    const handleTouchEnd = () => setIsDragging(false);

    window.addEventListener("touchmove", handleTouchMove, { passive: false });
    window.addEventListener("touchend", handleTouchEnd);
    return () => {
      window.removeEventListener("touchmove", handleTouchMove);
      window.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, id, onDrag, sceneSize, objectWidth, objectHeight]);

  // Long-press for mobile context menu
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleTouchStartMenu = useCallback(
    (e: React.TouchEvent) => {
      if (!canControl) return;
      longPressTimer.current = setTimeout(() => setShowMenu(true), 600);
    },
    [canControl]
  );

  const handleTouchEndMenu = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!canControl) return;
      e.preventDefault();
      e.stopPropagation();
      setShowMenu((prev) => !prev);
    },
    [canControl]
  );

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (!canControl) return;
      e.preventDefault();
      e.stopPropagation();
      setShowMenu(true);
    },
    [canControl]
  );

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (itemRef.current && !itemRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  return (
    <div
      ref={itemRef}
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: objectWidth,
        height: objectHeight,
        cursor: canControl ? (isDragging ? "grabbing" : "grab") : "default",
        zIndex: isDragging ? 1001 : 12,
        userSelect: "none",
        WebkitUserSelect: "none",
        touchAction: "none",
      }}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      onTouchStart={(e) => {
        handleTouchStartMenu(e);
        handleTouchStart(e);
      }}
      onTouchEnd={handleTouchEndMenu}
      onContextMenu={handleContextMenu}
    >
      <img
        src={`/assets/objects/${objectId}.png`}
        alt={name || `Objeto ${objectId}`}
        style={{ width: objectWidth, height: objectHeight, objectFit: "contain", display: "block" }}
        draggable={false}
      />
      {name && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: 2,
            background: "rgba(0,0,0,0.55)",
            color: "#fff",
            fontSize: isMobile ? 9 : 11,
            fontWeight: 600,
            borderRadius: 4,
            padding: "1px 5px",
            whiteSpace: "nowrap",
            pointerEvents: "none",
          }}
        >
          {name}
        </div>
      )}

      {/* Context menu */}
      {showMenu && canControl && (
        <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onTouchStart={(e) => {
            e.stopPropagation();
          }}
          style={{
            position: "absolute",
            top: -8,
            left: objectWidth + 4,
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            zIndex: 1000,
            minWidth: 120,
            padding: "4px 0",
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: "8px 12px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#dc2626",
              fontSize: 13,
              fontWeight: 600,
              gap: 6,
            }}
          >
            Remover
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(false);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              padding: "8px 12px",
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#6b7280",
              fontSize: 13,
            }}
          >
            Fechar
          </button>
        </div>
      )}
    </div>
  );
};

export default SceneObjectItem;
